import React from 'react'
import { Button, Card, CopyIcon, Stack, Switch } from '@caudena/web-platform'
import { Container, Tabs } from '@caudena/core/ui-kit'
import { ErrorBoundaryContainer } from '@caudena/core/ErrorBoundary'
import { ClientWidget } from '../../Widgets/ClientWidget'
import { CounterpartyWidget } from '../../Widgets/CounterpartyWidget'
import { ReportWidget } from '../../Widgets/ReportWidget'
import { useRightOverlapSidebarStateSetter } from '@caudena/core/state'
import OverlapSidebar, { OverlapSidebarVariant } from '../../OverlapSidebar'
import { RightOverlapSidebarSlot } from '@caudena/core/Layout/slots'
import { observer } from 'mobx-react-lite'
import { toJS } from 'mobx'
import * as S from './IncidentDetailsTabs.styles'
import { incidentViewModelFacade } from '@compliance/di/containers/IncidentViewModelFacade.container'

const tabs: { children: string; value: string }[] = [
  {
    children: 'Counterparty',
    value: 'cp',
  },
  {
    children: 'Client',
    value: 'client',
  },
  {
    children: 'Report',
    value: 'report',
  },
]

export const IncidentDetailsTabs = observer(() => {
  const {
    incidentData: mobxData,
    isFirstLoading: isIncidentFirstLoading,
    clientData: mobxClientData,
    isClientDataFirstLoading,
    relatedCases: mobxRelatedCases,
    relatedIncidents: mobxRelatedIncidents,
    aiSummary,
    aiSummaryStatus,
    isSummaryReportCantBeModified,
    isSummaryReportProcessing,
    regenerateReport,
  } = incidentViewModelFacade

  const aiSummaryData = toJS(aiSummary)
  const aiSummaryReportStatus = toJS(aiSummaryStatus)
  const data = toJS(mobxData)
  const clientData = toJS(mobxClientData)
  const relatedCases = toJS(mobxRelatedCases)
  const relatedIncidents = toJS(mobxRelatedIncidents)
  const [tab, setTab] = React.useState('cp')
  const isShowSummaryReportActions =
    tab === 'report' && !isSummaryReportCantBeModified

  const [currentView, setCurrentView] = React.useState<'snapshot' | 'current'>(
    'snapshot'
  )
  const setIsRightOverlapSidebarOpen = useRightOverlapSidebarStateSetter()

  const [overlapSidebarVariant, setOverlapSidebarVariant] =
    React.useState<OverlapSidebarVariant>('cases')

  const toggleRightOverlapSidebar = (variant: OverlapSidebarVariant) => {
    setOverlapSidebarVariant(variant)
    setIsRightOverlapSidebarOpen((prev) => !prev)
  }

  RightOverlapSidebarSlot.useContent(
    ({ toggle, isOpen }) => (
      <OverlapSidebar
        toggle={toggle}
        isOpen={isOpen}
        variant={overlapSidebarVariant}
        clientIncidents={relatedIncidents}
        clientCases={relatedCases}
      />
    ),
    [relatedIncidents, relatedCases, overlapSidebarVariant]
  )

  const tabsContent = {
    client: (
      <ClientWidget
        data={clientData}
        isFirstLoading={isClientDataFirstLoading}
        relatedIncidentsCount={relatedIncidents?.length}
        relatedCasesCount={relatedCases?.length}
        onRelatedCasesClick={() => toggleRightOverlapSidebar('cases')}
        onRelatedIncidentsClick={() => toggleRightOverlapSidebar('incidents')}
      />
    ),
    cp: (
      <CounterpartyWidget
        view={currentView}
        loading={isIncidentFirstLoading}
        counterparty={data?.counterparty?.data}
        exposure={data?.counterparty?.exposure}
        exposureSnapshot={data?.counterparty?.snapshotExposure}
        currency={data?.transaction?.currency}
      />
    ),
    report: (
      <ReportWidget data={aiSummaryData} status={aiSummaryReportStatus} />
    ),
  }
  return (
    <Card flex fullHeight borderRadius={'md'} withHeader={false}>
      <S.TabsWrapper>
        <Tabs options={tabs} value={tab} onChange={setTab} />
        {isShowSummaryReportActions && (
          <Container gap={[1, 1]}>
            <Stack gap={'md'} align={'center'} justify={'end'}>
              <Button
                isLoading={isSummaryReportProcessing}
                disabled={isSummaryReportProcessing}
                endIcon={'Renew'}
                size={'sm'}
                onClick={regenerateReport}
                variant={'outline'}
                color={'secondary'}
              >
                Regenerate
              </Button>
              <CopyIcon size={'sm'} value={aiSummaryData} variant={'outline'} />
            </Stack>
          </Container>
        )}
        {tab === 'cp' && (
          <Container gap={[1, 1]}>
            <Switch
              type="double"
              value={currentView}
              onChange={setCurrentView}
              leftLabel="Snapshot"
              rightLabel="Current"
              leftValue="snapshot"
              rightValue="current"
            />
          </Container>
        )}
      </S.TabsWrapper>
      <ErrorBoundaryContainer>{tabsContent[tab]}</ErrorBoundaryContainer>
    </Card>
  )
})
